import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Title, Meta } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { CanonicalService } from '../../../shared/seo/canonical.service';

@Component({
  selector: 'app-whatsapp-apis',
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './whatsapp-apis.component.html',
  styleUrls: ['./whatsapp-apis.component.scss'],
   encapsulation: ViewEncapsulation.None  // ✅ Make sure this line is here
})
export class WhatsappApisComponent implements OnInit {
  constructor(
    private titleService: Title,
    private metaService: Meta,
    private canonicalService: CanonicalService
  ) {}

  ngOnInit(): void {
    // Set page title
    this.titleService.setTitle('WhatsApp API | Secure Messaging by MALtech');

    // Set meta description
    this.metaService.updateTag({
      name: 'description',
      content:
        'MALtech provides WhatsApp APIs to integrate messaging services, send notifications, alerts, and engage customers efficiently.',
    });

    // Set meta keywords
    this.metaService.updateTag({
      name: 'keywords',
      content:
        'WhatsApp API, Messaging API, Notifications, Customer Engagement, MALtech',
    });

    // Optional: SEO enhancements
    this.metaService.updateTag({ name: 'robots', content: 'index, follow' });

    this.canonicalService.setCanonicalURL('product/whatsapp-apis/');
  }
  features = [
    {
      icon: 'bi bi-graph-up-arrow',
      title: 'Scalable for Every Stage',
      desc: 'From startups sending a few hundred messages to enterprises handling millions of conversations, MALtech grows with your business without breaking performance.',
    },
    {
      icon: 'bi bi-shield-lock',
      title: 'Secure by Design',
      desc: 'Enterprise-grade protection with end-to-end encryption, GDPR, HIPAA, and ISO 27001 compliance, ensuring your customer data is always safe.',
    },
    {
      icon: 'bi bi-lightning-charge',
      title: 'Faster Go-Live',
      desc: 'Get your WhatsApp Business account verified and live in days, not weeks.',
    },
    {
      icon: 'bi bi-headset',
      title: '24/7 Multi-Channel Support',
      desc: 'Live chat, email, and phone support across time zones — because customer engagement never sleeps.',
    },
    {
      icon: 'bi bi-plug',
      title: 'Seamless Integrations',
      desc: 'Plug into 100+ global CRMs, ERPs, and Helpdesks (Salesforce, Zoho, HubSpot, SAP, Freshdesk & more).',
    },
    {
      icon: 'bi bi-people',
      title: 'Personalized Engagement at Scale',
      desc: 'Deliver localized, multilingual, and targeted messages that feel personal — even when you’re reaching millions of customers worldwide.',
    },
  ];

  selectedCurrency: 'USD' | 'INR' = 'USD';

  plans = {
    USD: [
      {
        name: 'Starter',
        price: 49,
        features: ['1,000 messages', 'Basic API Access', 'Email Support'],
      },
      {
        name: 'Growth',
        price: 149,
        features: [
          '10,000 messages',
          'Dashboard + Reports',
          'Priority Support',
        ],
      },
      {
        name: 'Pro',
        price: 299,
        features: [
          '50,000 messages',
          'Multi-agent Inbox',
          'CRM & ERP Integrations',
        ],
        popular: true,
      },
    ],
    INR: [
      {
        name: 'Starter',
        price: 3999,
        features: ['1,000 messages', 'Basic API Access', 'Email Support'],
      },
      {
        name: 'Growth',
        price: 11999,
        features: [
          '10,000 messages',
          'Dashboard + Reports',
          'Priority Support',
        ],
      },
      {
        name: 'Pro',
        price: 23999,
        features: [
          '50,000 messages',
          'Multi-agent Inbox',
          'CRM & ERP Integrations',
        ],
        popular: true,
      },
    ],
  };
  activeIndex = -1;

  setActive(i: number) {
    this.activeIndex = i;
  }

  clearActive() {
    this.activeIndex = -1;
  }

  approach = [
    {
      title: 'Consult & Understand',
      desc: 'Understand local industry needs',
      icon: 'bi-search',
    },
    {
      title: 'Onboard & Verify',
      desc: 'Support with Facebook Business verification',
      icon: 'bi-check-circle',
    },
    {
      title: 'Integrate & Customize',
      desc: 'Tailored to Indian CRM & ERP systems',
      icon: 'bi-gear',
    },
    {
      title: 'Integration & Deployment',
      desc: 'Seamlessly integrating and deploying your branded solutions.',
      icon: 'bi-arrow-left-right',
    },
    {
      title: 'Optimize & Support',
      desc: 'Continuous analytics + 24/7 support in IST',
      icon: 'bi-graph-up',
    },
  ];

  supports = [
    {
      title: '24/7 Support',
      icon: 'bi-clock',
      link: '/contact',
      newTab: false,
    },
    {
      title: 'Phone Support',
      icon: 'bi-telephone',
      link: 'tel:+6369765570',
      newTab: false,
    },
    {
      title: 'Email Support',
      icon: 'bi-envelope',
      link: 'https://mail.google.com/mail/?view=cm&fs=1&to=support@maltech.co',
      newTab: true,
    },
    {
      title: 'WhatsApp Support',
      icon: 'bi-whatsapp', // official WhatsApp icon
      link: 'https://wa.me/6369765570',
      newTab: true,
    },
  ];

  scrollToPlans() {
    const plansSection = document.getElementById('features');
    if (plansSection) {
      plansSection.scrollIntoView({ behavior: 'smooth' });
    }
  }
}
